import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ClientSidebar from '../components/Dashboard/ClientSidebar';
import HistorySidebar from '../components/Dashboard/HistorySidebar';
import WebRTCVideoChat from '../components/WebRTCVideoChat';
import { userAPI, getFullUrl } from '../services/api';
import { useAuth } from '../services/AuthContext';
import RatingModal from '../components/Modals/RatingModal';

const VideoCoach = () => {
  const { user: currentUser } = useAuth();
  const { coachId } = useParams();
  const [coach, setCoach] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogId, setDialogId] = useState(null);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [coachRelation, setCoachRelation] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    const fetchCoachAndRelation = async () => {
      try {
        setLoading(true);
        const response = await userAPI.getById(coachId);
        setCoach(response.data.user);

        if (currentUser?.role === 'client') {
          const myCoachesRes = await userAPI.getMyCoaches();
          const myCoaches = Array.isArray(myCoachesRes.data) ? myCoachesRes.data : [];
          const relation = myCoaches.find(c => c.id === coachId);
          if (relation) {
            setCoachRelation(relation);
            if (!relation.isApproved || relation.status !== 'active') {
              setIsBlocked(true);
            }
          } else {
            setIsBlocked(true);
          }
        }
      } catch (err) {
        console.error('Error fetching coach:', err);
        setError('Coach not found');
      } finally {
        setLoading(false);
      }
    };
    if (coachId && currentUser) fetchCoachAndRelation();
  }, [coachId, currentUser]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--bg-main)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--tangerine)]"></div>
      </div>
    );
  }

  if (error || !coach) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[var(--bg-main)] p-12 text-center">
        <span className="material-symbols-outlined text-6xl text-gray-300 mb-4">videocam_off</span>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Coach not found</h2>
        <Link to="/user/coaches" className="text-[var(--tangerine)] font-bold mt-4 underline">Back to Coaches</Link>
      </div>
    );
  }

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      <div className="layout-container flex h-full grow flex-col">
        <div className="px-2 py-2 w-full flex justify-center">
          <div className="layout-content-container flex flex-col xl:flex-row w-full max-w-[1700px] flex-1 gap-4">

            <HistorySidebar coachId={coach.chatId || coach.id} />

            <main className="flex-1 flex flex-col min-w-0 items-center justify-start p-0 pt-2">
              <div
                className="h-[calc(100vh-100px)] w-auto bg-black rounded-[2.5rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] relative border border-[var(--border-light)] transition-all duration-700 mx-auto"
                style={{ aspectRatio: '9/16' }}
              >
                {isBlocked ? (
                  <div 
                    className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center z-20"
                    style={{ backgroundColor: '#111827' }}
                  >
                    <div className="w-16 h-16 bg-orange-500/10 rounded-full flex items-center justify-center text-tangerine mb-6 animate-pulse border border-orange-500/20">
                      <span className="material-symbols-outlined text-3xl">lock</span>
                    </div>
                    <h3 className="text-xl font-black text-white mb-3">Subscription Inactive</h3>
                    <p className="text-gray-300 text-xs max-w-xs mb-8 leading-relaxed">
                      {coachRelation?.path === 'A' 
                        ? "Your coach covers your access, but their subscription is currently inactive. Please contact your coach to restore access." 
                        : `Your monthly subscription is inactive. Please pay $${coachRelation?.price || '120'}/month to start communicating with your coach's avatar.`}
                    </p>
                    {coachRelation?.path === 'B' && (
                      <a 
                        href="/user/billing"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-tangerine hover:bg-[#d97700] text-white font-black text-xs uppercase tracking-widest rounded-xl shadow-lg shadow-orange-500/20 transition-all hover:-translate-y-0.5 active:scale-95 animate-fade-in"
                      >
                        <span className="material-symbols-outlined text-sm">credit_card</span>
                        Go to Billing
                      </a>
                    )}
                  </div>
                ) : isStarted ? (
                  <WebRTCVideoChat
                    taskId={currentUser?.id || 'guest'}
                    videoPath={getFullUrl(coach.avatar?.url)}
                    thumbnailUrl={getFullUrl(coach.avatar?.thumbnailUrl)}
                    videoHelloText={coach.videoWelcomeMessage || `Hello, I am ${coach.name}. Let's begin our session.`}
                    coachId={coach.chatId || coach.id}
                    voiceSettings={coach.voiceSettings}
                    onStatusChange={(msg, type) => console.log(`[Streaming] ${type}: ${msg}`)}
                    onDialogCreated={(id) => setDialogId(id)}
                    onEnd={() => setIsRatingModalOpen(true)}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-center z-20 overflow-hidden bg-black/40 backdrop-blur-sm">
                    {/* Ambient Background Image */}
                    {(coach.avatar?.thumbnailUrl || coach.avatarUrl) && (
                      <div className="absolute inset-0 z-0">
                        <img
                          src={getFullUrl(coach.avatar?.thumbnailUrl || coach.avatarUrl)}
                          alt=""
                          className="w-full h-full object-cover opacity-60"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/80"></div>
                      </div>
                    )}

                    <div className="relative z-10 w-full animate-fade-in">
                      <div className="mb-8 relative transition-transform hover:scale-105 duration-500">
                        <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 border-white/20 shadow-2xl mb-4 bg-gray-900 flex items-center justify-center">
                          {coach.avatarUrl ? (
                            <img
                              src={getFullUrl(coach.avatarUrl)}
                              alt={coach.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="material-symbols-outlined text-4xl text-gray-400">person</span>
                          )}
                        </div>
                        <h2 className="text-3xl font-black text-white tracking-tight mb-2">{coach.name}</h2>
                        <p className="text-[var(--tangerine)] text-xs font-black uppercase tracking-[0.3em]">{coach.specialization || "AI Coaching Companion"}</p>
                      </div>

                      <div className="flex flex-col items-center gap-6">

                        <button
                          onClick={() => setIsStarted(true)}
                          className="group relative flex items-center justify-center gap-3 bg-[var(--tangerine)] hover:bg-[var(--tangerine)]/90 text-white px-10 py-5 rounded-2xl font-black text-sm uppercase tracking-widest shadow-2xl shadow-orange-500/40 transition-all hover:-translate-y-1 active:scale-95"
                        >
                          <span className="material-symbols-outlined text-xl group-hover:animate-pulse">play_circle</span>
                          Start Conversation
                        </button>
                        <p className="text-[10px] font-bold text-white/30 uppercase tracking-[0.2em] mt-4">
                          Ready to begin your session?
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* UI Overlays for aesthetic polish when started */}
                {isStarted && (
                  <div className="absolute top-6 left-6 flex items-center gap-2 pointer-events-none animate-fade-in z-30">
                    <div className="px-3 py-1 bg-black/40 backdrop-blur-md rounded-lg border border-white/20 text-[10px] font-black text-white uppercase tracking-widest">
                      Live 4K
                    </div>
                  </div>
                )}
              </div>
            </main>

            <ClientSidebar selectedCoach={coach} activeTab="video" />
          </div>
        </div>
      </div>
      <RatingModal 
        isOpen={isRatingModalOpen} 
        onClose={() => setIsRatingModalOpen(false)} 
        dialogId={dialogId} 
      />
    </div>
  );
};

export default VideoCoach;
